<template>
  <my-giving-layout :enable-scroll-event="true">
    <div class="hogc-theme-campaign bg-campaign-surface w-full min-h-full">
      <PledgeHeader :back="back">
        <template v-if="closed">Pledging is closed</template>
        <template v-else>I want to be a part<br />of history</template>
      </PledgeHeader>
      <div
        class="px-[16px] md:px-[24px] lg:px-[28px] pt-[24px] pb-[48px] md:pb-[56px] lg:pb-[64px]"
        :style="hasFocusedField ? `padding-bottom: ${keyboardInsetCss}` : ''"
      >
        <!-- Not eligible and no pledge to show: the campaign is closed. Placeholder
             copy until design answers Q6 in the plan. -->
        <div
          v-if="closed"
          class="w-full md:max-w-2xl md:mx-auto flex flex-col gap-[20px]"
          data-test="bf-closed"
        >
          <DsBody class="text-text-body-md text-campaign-ink"
            >The BF2026 pledge is not open right now.</DsBody
          >
          <DsButton variant="campaign" size="52" class="w-full" @click="back"> Back </DsButton>
        </div>
        <div v-else class="w-full md:max-w-2xl md:mx-auto flex flex-col gap-[20px]">
          <!-- One-time, OR the months, AND / OR sow: the physical card's order (Card
               V5). The one-time amount and the months exclude each other: whichever
               has an amount greys out the other card (P1a, P1b). -->
          <DsCard
            ref="oneTimeCard"
            :class="['flex flex-col gap-[16px]', monthsActive && disabledCardClass]"
            data-test="bf-one-time-card"
          >
            <PledgeCardTitle tone="oneTime" :muted="monthsActive"
              >I am ready to give a one-time amount of:</PledgeCardTitle
            >
            <PledgeTotalField
              :modelValue="form.oneTime"
              name="one_time_commitment"
              aria-label="One-time amount"
              :disabled="monthsActive"
              :error="amountError('oneTime')"
              data-test="bf-one-time"
              @update:modelValue="onOneTimeInput"
              @blur="onOneTimeBlur"
            />
          </DsCard>

          <DsBadge variant="campaign" :class="campaignChipClass">OR</DsBadge>

          <!-- Months -->
          <DsCard
            ref="monthsCard"
            :class="['flex flex-col gap-[16px]', oneTimeActive && disabledCardClass]"
            data-test="bf-months-card"
          >
            <PledgeCardTitle :muted="oneTimeActive"
              >Over the next 5 months,<br />I commit to giving:</PledgeCardTitle
            >
            <!-- The total, above the months as P1 and P1b have it, takes input too: a
                 total typed here is split evenly over the five months, and changing a
                 month then moves the others so they still add up to it. -->
            <PledgeTotalField
              :modelValue="totalField"
              name="total"
              aria-label="Total amount over 5 months"
              :disabled="oneTimeActive"
              :error="showTotalError ? errorCopy.amount : ''"
              data-test="bf-month-total"
              @update:modelValue="onTotalInput"
              @blur="onTotalBlur"
            />
            <!-- The month fields are 48px tall in P1 and P1b; disabled, they are flat
                 grey boxes like the Total (P1a). The "$" sits 4px from the amount with
                 no divider, so DsInput's 8px gaps and its divider are overridden. -->
            <div
              class="flex flex-wrap gap-x-[12px] gap-y-[14px] [--hogc-field-padding:11px_12px] [--hogc-field-disabled-bg:theme(colors.grey.10)] [--hogc-field-disabled-border-color:transparent] [--hogc-field-disabled-text-color:theme(colors.grey.30)] [&_.gap-2]:!gap-[4px] [&_span.w-px]:hidden [&_span.whitespace-nowrap]:font-semibold"
            >
              <div
                v-for="month in months"
                :key="month.key"
                class="flex flex-col gap-[6px] w-[calc(50%-6px)]"
                data-bf-month
              >
                <DsBadge
                  variant="campaign-soft"
                  :class="['self-start', monthChipClass(month.key)]"
                  data-bf-month-chip
                >
                  {{ month.label }}
                </DsBadge>
                <DsInput
                  :modelValue="form.months[month.key]"
                  prefix="$"
                  placeholder="–"
                  :name="month.key"
                  :aria-label="`${month.label} amount`"
                  :class="!monthHasAmount(month.key) && emptyAmountFieldClass"
                  data-bf-amount
                  :disabled="oneTimeActive"
                  :error="amountError(month.key)"
                  :data-test="`bf-month-${month.key}`"
                  @input.capture="sanitizeAmountField"
                  @update:modelValue="onMonthInput(month.key, $event)"
                  @blur="onAmountBlur(month.key)"
                />
              </div>
            </div>
          </DsCard>

          <DsBadge variant="campaign" :class="campaignChipClass">AND / OR</DsBadge>

          <!-- Sow -->
          <DsCard class="flex flex-col gap-[12px]" data-test="bf-sow-card">
            <PledgeCardTitle tone="sow">I pledge to sow...</PledgeCardTitle>
            <!-- The verse as P1b gives it, still ending in an ellipsis. -->
            <div
              class="px-[14px] py-[10px] border-l-[3px] border-solid border-campaign-quote-rule bg-campaign-surface rounded-r-[10px]"
            >
              <DsBody class="text-[14px] leading-[20px] italic text-campaign-muted">
                2 Corinthians 9:10 (NKJV)<br />“Now may He who <b>supplies seed to the sower</b>,
                and bread for food, supply and multiply the seed you have sown and increase the
                fruits of your righteousness…”
              </DsBody>
            </div>
            <DsTextarea
              v-model="form.sow"
              name="sow_pledge"
              aria-label="I pledge to sow"
              :rows="4"
              placeholder="e.g. bonus, commission, extra / unexpected blessings"
              data-test="bf-sow"
            />
          </DsCard>

          <!-- Youth. The two exclude each other, as on the web pledge form: each is
               hidden (and disabled) while the other is ticked — P1b hides this one,
               and the couple row goes while this is ticked (2 Oct). -->
          <!-- Drawn as W3 draws it: the same tinted row as the couple one below —
               18px/20px inset, round 24px tick — so the two ticks share one centre
               line (ONE-8664). The label splits into a bold line and a 14px note. -->
          <DsCard
            v-show="!form.couple"
            class="flex flex-col gap-[12px] border border-solid border-campaign-line !bg-campaign-divider [--hogc-card-padding:18px_20px] [--hogc-checkbox-radius:9999px]"
            data-test="bf-youth-card"
          >
            <!-- `!h-auto !mb-0`: FormInput.vue's unscoped `label { height: 24px }` leaks
                 onto every <label> in the app and cropped this one to a single line. -->
            <DsCheckbox
              v-model="form.isYouth"
              size="lg"
              :disabled="form.couple"
              class="!h-auto !mb-0"
              data-test="bf-youth"
            >
              <span class="block text-[16px] leading-[22px] font-bold">
                Tick if you are currently in Secondary School OR aged 16 and below.
              </span>
              <span class="block mt-[6px] text-[14px] leading-[20px] font-normal">
                Amount given should not exceed $200 a month, unless you have parental consent.
              </span>
            </DsCheckbox>
            <DsAlert v-if="youthOverCap" variant="warning" data-test="bf-youth-notice">
              {{ youthNotice }}
            </DsAlert>
          </DsCard>

          <!-- A youth is asked on its own row, under the youth tick, whether they
               pledge one-time (P1a) or over the months. P1b drew the months case
               inside the five-month card; moved here per stakeholder review (Oct). -->
          <PledgeFirstTimeTick v-if="asksFirstTime" v-model="form.firstTime" />

          <!-- Couple. Ticked, it grows the partner consent line and the partner's
               details. Ticking it is the partner consent. -->
          <!-- P1 draws the row tinted; ticked (P1b) it turns navy, with the partner's
               fields below it. The tick is round there, as on the consent. -->
          <DsCard
            v-show="!form.isYouth"
            ref="coupleCard"
            padding="none"
            :class="[
              'flex flex-col overflow-hidden border-solid',
              form.couple
                ? 'border-[1.5px] border-campaign-primary'
                : 'border border-campaign-line !bg-campaign-divider',
            ]"
            data-test="bf-couple-card"
          >
            <div
              :class="[
                'flex items-center gap-[14px] px-[20px] py-[18px] [--hogc-checkbox-radius:9999px]',
                form.couple ? coupleRowTickedClass : 'text-campaign-ink',
              ]"
              data-test="bf-couple-row"
            >
              <!-- `!h-auto !mb-0` as on the youth tick. -->
              <DsCheckbox
                v-model="form.couple"
                label="I'd like to pledge as a couple"
                size="lg"
                :disabled="form.isYouth"
                class="flex-1 !h-auto !mb-0"
                data-test="bf-couple"
              />
              <button
                type="button"
                class="shrink-0 flex"
                aria-label="What is pledging as a couple?"
                data-test="bf-couple-info"
                @click="coupleSheetOpen = true"
              >
                <DsIcon name="info" size="md" />
              </button>
            </div>
            <!-- v-show, not v-if: mounted() stamps every text field with
                 data-compact-input, and a field created later would miss it. What
                 was typed is kept if the box is unticked and ticked again. -->
            <div
              v-show="form.couple"
              class="flex flex-col gap-[16px] px-[16px] pt-[18px] pb-[20px]"
              data-test="bf-partner-fields"
            >
              <DsCaption
                class="text-[12px] leading-[18px] italic text-campaign-muted"
                data-test="bf-partner-consent"
              >
                By selecting this option, I confirm that I have the consent of my partner to pledge
                as a couple.
              </DsCaption>
              <DsInput
                v-model="form.partnerName"
                label="Name of partner"
                placeholder="Enter full name"
                name="partner_name"
                :class="wrappingLabelClass"
                :error="partnerError('name')"
                data-test="bf-partner-name"
                @blur="onPartnerBlur('name')"
              />
              <!-- Errors show after Submit or once a date is picked: opening the
                   calendar blurs the field, so a blur would flag it before a pick. -->
              <DsDatePicker
                v-model="form.partnerDob"
                label="Partner's date of birth"
                placeholder="DD-MM-YYYY"
                display-format="DD-MM-YYYY"
                name="partner_dob"
                :class="wrappingLabelClass"
                :max="today"
                :error="partnerError('dob')"
                data-test="bf-partner-dob"
                @change="onPartnerBlur('dob')"
              />
              <!-- Uppercased for display only; the personal ID is uppercased when
                   the body is built. -->
              <DsInput
                v-model="form.partnerLast4"
                label="Partner's last 4 characters of NRIC/FIN/Passport no."
                placeholder="e.g. 123A"
                name="partner_last4"
                :maxLength="4"
                :error="partnerError('last4')"
                :class="[
                  wrappingLabelClass,
                  '[&_input]:uppercase [&_input::placeholder]:normal-case',
                ]"
                data-test="bf-partner-last4"
                @blur="onPartnerBlur('last4')"
              />
              <DsLink
                variant="campaign"
                underline="always"
                size="sm"
                class="self-start inline-flex items-center gap-[6px]"
                data-test="bf-partner-info"
                @click="partnerSheetOpen = true"
              >
                <DsIcon name="info" size="sm" />
                What is this information used for?
              </DsLink>
            </div>
          </DsCard>

          <!-- Vueform's later `.border-0 { border: 0 }` wipes the DS line's width, style
               and colour, so all three are set again. -->
          <DsDivider class="!border-t !border-solid !border-campaign-divider" />

          <!-- Personal details -->
          <div class="flex flex-col gap-[16px]">
            <DsHeading
              tag="h2"
              class="text-[24px] leading-[28px] font-black tracking-[-0.01em] text-campaign-ink"
            >
              Confirm personal details
            </DsHeading>
            <DsBody class="text-text-body-md text-campaign-ink">
              If your Full Name or Mobile Number is inaccurate, you can edit it. If your CG is not
              up to date, ask your leaders to help update it.
            </DsBody>
            <!-- As on the paper card: what is typed here goes on the pledge only, never
                 the profile. The same fields read-only and editing, so the element
                 mounted() stamped is the one typed into. The CG stays read-only: leaders
                 update it, as the copy above says. -->
            <!-- The detail fields are 56px tall in P1 and P1b. Editing, the mobile is
                 a country picker and the national number; it is kept as
                 `+<calling code> <number>` (`+65 91234567`), the format giving-api
                 stores, and shown that way read-only. -->
            <template v-for="field in detailFields" :key="field.key">
              <PhoneCountryInput
                v-if="field.key === 'mobileNumber' && editingDetails"
                :label="field.label"
                :name="field.name"
                :country="mobileCountry"
                :number="mobileNational"
                :error="mobileError"
                :data-test="field.test"
                @update:country="onMobileCountry"
                @update:number="onMobileNumber"
                @blur="onMobileBlur"
              />
              <DsInput
                v-else
                class="[--hogc-field-padding:15px_16px]"
                :label="field.label"
                :modelValue="form.details ? form.details[field.key] : prefilledDetails[field.key]"
                :readOnly="!editingDetails || field.locked"
                :name="field.name"
                :error="field.key === 'mobileNumber' ? mobileError : ''"
                :data-test="field.test"
                @update:modelValue="form.details[field.key] = $event"
              />
            </template>
            <div class="flex justify-end">
              <DsButton
                variant="campaign-outline"
                size="36"
                :data-test="editingDetails ? 'bf-save-details' : 'bf-edit-details'"
                @click="editingDetails ? saveDetails() : editDetails()"
              >
                {{ editingDetails ? 'Save details' : 'Edit details' }}
              </DsButton>
            </div>
          </div>

          <!-- Privacy -->
          <DsCard class="flex flex-col gap-[12px]" data-test="bf-privacy-card">
            <DsBody tag="span" class="text-text-body-md font-bold text-campaign-ink"
              >🔒 Privacy Policy</DsBody
            >
            <DsBody class="text-[14px] leading-[20px] text-campaign-ink">
              By providing this information, you agree that Heart of God Church may collect, use and
              disclose your personal data as provided here or obtained by our organisation as a
              result of your interaction with us in accordance with our Privacy Policy. For more
              details of our Privacy Policy, please visit our website at
              <DsLink
                variant="campaign"
                underline="always"
                size="sm"
                data-test="bf-privacy-link"
                @click="openPrivacy"
                >privacy.heartofgodchurch.org</DsLink
              >.
            </DsBody>
            <DsCheckbox
              v-model="form.consent"
              label="I consent"
              size="lg"
              class="!h-auto !mb-0 [--hogc-checkbox-radius:9999px]"
              data-test="bf-consent"
            />
          </DsCard>

          <!-- Need Help? is hidden in P1 and P1b (display: none), so it is not built. -->
          <DsButton
            variant="campaign"
            size="52"
            class="w-full"
            :disabled="!canSubmit"
            data-test="bf-submit"
            @click="submit"
          >
            Continue
          </DsButton>
        </div>
      </div>
    </div>
    <PledgeInfoSheet
      v-model="coupleSheetOpen"
      title="Pledging as a couple"
      test-id="bf-couple-sheet"
    >
      <DsBody class="text-text-body-md text-campaign-ink">
        One pledge for you and your spouse.<br />
        Only one of you needs to submit the pledge. The pledge will be reflected on their MyGiving
        after their details are verified.
      </DsBody>
    </PledgeInfoSheet>
    <PledgeInfoSheet
      v-model="partnerSheetOpen"
      title="What is this information used for?"
      large
      test-id="bf-partner-sheet"
    >
      <DsBody class="text-text-body-md text-campaign-ink">
        We use your partner's name, date of birth and partial NRIC/FIN/Passport no. to accurately
        identify them, so your couple pledge is recorded and attributed to both of your accounts.
      </DsBody>
      <DsBody class="text-text-body-md text-campaign-ink">
        Rest assured, this information is handled securely and is only used for this purpose.
      </DsBody>
    </PledgeInfoSheet>
  </my-giving-layout>
</template>

<script>
import { mapGetters } from 'vuex';
import {
  Alert as DsAlert,
  Badge as DsBadge,
  Body as DsBody,
  Button as DsButton,
  Caption as DsCaption,
  Card as DsCard,
  Checkbox as DsCheckbox,
  DatePicker as DsDatePicker,
  Divider as DsDivider,
  Heading as DsHeading,
  Icon as DsIcon,
  Input as DsInput,
  Link as DsLink,
  Textarea as DsTextarea,
} from '@team-hogc/hogc-one-design-system';
import MyGivingLayout from '@/components/MyGivingLayout.vue';
import keyboardFieldIntoView from '@/mixins/keyboard-field-into-view';
import PledgeCardTitle from '@/components/MyGiving/BuildingFundPledge/PledgeCardTitle.vue';
import PledgeFirstTimeTick from '@/components/MyGiving/BuildingFundPledge/PledgeFirstTimeTick.vue';
import PledgeHeader from '@/components/MyGiving/BuildingFundPledge/PledgeHeader.vue';
import PledgeInfoSheet from '@/components/MyGiving/BuildingFundPledge/PledgeInfoSheet.vue';
import PledgeTotalField from '@/components/MyGiving/BuildingFundPledge/PledgeTotalField.vue';
import PhoneCountryInput from '@/components/common/PhoneCountryInput.vue';
import {
  BF_PLEDGE_ERRORS,
  BF_PLEDGE_MONTHS,
  BF_PLEDGE_PRIVACY_URL,
  BF_PLEDGE_ROUTES,
  BF_SOW_MAX_LENGTH,
  BF_YOUTH_NOTICE,
} from '@/constants/MyGiving/building-fund-pledge';
import { ERROR_VALID_MOBILE } from '@/constants/validation/error-messages';
import { sanitizeAmountInput } from '@/utils/amount-input';
import { openUrl } from '@/utils/browser-helper';
import {
  DEFAULT_DIAL_CODE,
  dialCodeForCountry,
  formatContactNumber,
  iso2ForDialCode,
  splitContactNumber,
} from '@/utils/phone';
import {
  asksFirstTime,
  emptyPledgeForm,
  formatAmount,
  isBfPledgeOpen,
  isoDate,
  isYouthByAge,
  monthTotal,
  parseAmount,
  pledgesOneTime,
  rebalanceMonths,
  splitTotal,
  validatePledgeForm,
  youthExceeded,
} from '@/utils/building-fund-pledge';

// The BF2026 digital pledge, laid out as the physical card (Card V5): a one-time
// amount OR five monthly amounts, AND/OR a sow commitment, then the couple and youth
// ticks, the member's details for them to check, and consent. What the member types
// stays on the page; Continue hands it to the buildingFundPledge store, which turns it
// into giving-api's request body, and opens the confirm page, which sends it (C1, C2,
// and C1 for a one-time pledge too). There is no Save: drafts were dropped on
// 2026-10-01.
export default {
  name: 'BuildingFundPledgeForm',

  mixins: [keyboardFieldIntoView],

  components: {
    DsAlert,
    DsBadge,
    DsBody,
    DsButton,
    DsCaption,
    DsCard,
    DsCheckbox,
    DsDatePicker,
    DsDivider,
    DsHeading,
    DsIcon,
    DsInput,
    DsLink,
    DsTextarea,
    MyGivingLayout,
    PledgeCardTitle,
    PledgeFirstTimeTick,
    PledgeHeader,
    PledgeInfoSheet,
    PledgeTotalField,
    PhoneCountryInput,
  },

  data() {
    return {
      form: emptyPledgeForm(),
      months: BF_PLEDGE_MONTHS,
      youthNotice: BF_YOUTH_NOTICE,
      errorCopy: BF_PLEDGE_ERRORS,
      // Errors show once Submit has been tapped, then track the form live so each
      // clears as soon as it is fixed. A bad amount also shows once its field is left.
      attempted: false,
      blurred: {},
      closed: false,
      editingDetails: false,
      // The mobile's country (ISO 2) while the details are edited: Singapore unless
      // the number holds another.
      mobileCountry: iso2ForDialCode(DEFAULT_DIAL_CODE),
      detailFields: [
        { key: 'fullName', label: 'Full Name', name: 'full_name', test: 'bf-full-name' },
        { key: 'mobileNumber', label: 'Mobile Number', name: 'mobile', test: 'bf-mobile' },
        {
          key: 'connectGroup',
          label: 'Connect Group',
          name: 'connect_group',
          test: 'bf-cg',
          locked: true,
        },
      ],
      coupleSheetOpen: false,
      partnerSheetOpen: false,
      // The latest date of birth the picker offers; a partner cannot be born later.
      today: isoDate(new Date()),
      // What is in the Total field. A typed total holds while months change; with
      // none typed, the field shows the months added up.
      totalInput: '',
      totalTyped: false,
      // Months typed in since the total was, which a rebalance leaves alone.
      editedMonths: [],
      // FormInput.vue's unscoped `label { height: 24px }` fixes every label to one
      // line, so a label that wraps (the last-4 one does on a phone) spills onto its
      // field.
      wrappingLabelClass: '[&_label]:!h-auto',
      // The card on the side of "one-time OR five months" that is not in use (P1a,
      // P1b): a paler card, its fields flat and grey.
      disabledCardClass: '!bg-grey-5',
      campaignChipClass: 'self-center',
      // An empty month field in the frames' warm neutrals (P0); an amount turns it
      // the campaign theme's blue (P1b).
      emptyAmountFieldClass: [
        '[--hogc-field-border-color:theme(colors.campaign-empty.line)]',
        '[--hogc-field-affix-color:theme(colors.campaign-empty.text)]',
        '[--hogc-field-placeholder-color:theme(colors.campaign-empty.text)]',
      ],
      // P1b's ticked couple row: navy, white label, a gold tick ringed in white.
      coupleRowTickedClass: [
        'bg-campaign-primary text-campaign-on-primary',
        '[--hogc-checkbox-label-color:var(--hogc-campaign-on-primary)]',
        '[--hogc-checkbox-checked-bg:var(--hogc-campaign-accent)]',
        '[--hogc-checkbox-checked-border-color:var(--hogc-campaign-on-primary)]',
        '[--hogc-checkbox-checked-color:var(--hogc-campaign-ink-strong)]',
      ],
    };
  },

  computed: {
    ...mapGetters('profile', ['fullName', 'cgName', 'localMobileNumber', 'countryCode']),

    // `+65 91234567`, however the profile stored the code (`65`, `+65`, `++65`).
    // A number on file with no code is taken as Singapore's.
    mobileNumber() {
      return formatContactNumber(this.countryCode || DEFAULT_DIAL_CODE, this.localMobileNumber);
    },

    mobileNational() {
      return splitContactNumber(this.form.details?.mobileNumber).nationalNumber;
    },

    mobileError() {
      return this.errors.mobile && (this.attempted || this.blurred.mobile)
        ? ERROR_VALID_MOBILE
        : '';
    },

    prefilledDetails() {
      return {
        fullName: this.fullName || '',
        mobileNumber: this.mobileNumber,
        connectGroup: this.cgName || '',
      };
    },

    errors() {
      const errors = validatePledgeForm(this.form);
      if (this.totalInvalid) errors.total = true;
      return errors;
    },

    totalField() {
      if (this.totalTyped) return this.totalInput;
      const sum = monthTotal(this.form.months);
      return sum ? formatAmount(String(sum)) : '';
    },

    // Which side of "one-time OR five months" the member has filled in. The other
    // side is disabled until this one is cleared.
    oneTimeActive() {
      return pledgesOneTime(this.form);
    },

    monthsActive() {
      return monthTotal(this.form.months) > 0 || parseAmount(this.totalInput) > 0;
    },

    totalInvalid() {
      return this.totalTyped && Number.isNaN(parseAmount(this.totalInput));
    },

    showTotalError() {
      return this.totalInvalid && (this.attempted || this.blurred.total);
    },

    // Submit stays disabled until something is pledged (a one-time amount, a month
    // or sow) and "I consent" is ticked, so neither has an error of its own.
    // Until the pledge window is open (BF_PLEDGE_OPEN_FLAG) the form still shows, but
    // Continue stays disabled, so nothing reaches Confirm.
    canSubmit() {
      return isBfPledgeOpen() && !this.errors.empty && !this.errors.consent;
    },

    youthByAge() {
      return isYouthByAge(this.$store.state.profile);
    },

    // The store sends a youth by age as a youth even with the box unticked, so they
    // see the over-the-cap notice too.
    youthOverCap() {
      return youthExceeded(this.form, this.youthByAge);
    },

    // A youth, by the tick or by age, is asked "I'm giving for the first time" once
    // one side has an amount; the store sends the answer only then.
    asksFirstTime() {
      return asksFirstTime(this.form, this.youthByAge);
    },
  },

  created() {
    // Usually a no-op: the profile is loaded at app start. It matters on a cold
    // deep link. Once Edit details is tapped the fields hold what the member types.
    this.$store.dispatch('profile/fetchProfile').catch(() => {});
  },

  ionViewWillEnter() {
    this.checkEligibility();
  },

  mounted() {
    this.markFields();
    this.startKeyboardOverlapTracking();
  },

  methods: {
    monthHasAmount(key) {
      return parseAmount(this.form.months[key]) > 0;
    },

    // A month's chip: grey while the month is empty (P0), gold once it has an amount
    // (P1b), paler grey while the one-time side is in use (P1a).
    monthChipClass(key) {
      if (this.oneTimeActive) return '!bg-grey-10 !text-grey-30';
      if (this.monthHasAmount(key)) return '';
      return '!bg-campaign-empty-chip !text-campaign-empty-chip-ink';
    },

    // DsInput and DsTextarea put extra attributes on their wrapper, not on the
    // field, so these have to be set on the element itself.
    //
    // `data-compact-input` opts out of main.scss's blanket text-input rule, whose
    // `#ffffff !important`, 8px radius and 55px height beat the DS field's own
    // `bg-transparent` — a white strip inside the tinted read-only boxes, and a
    // textarea squashed to 55px whatever its `rows`.
    markFields() {
      this.$el
        .querySelectorAll?.('input[type="text"], textarea')
        .forEach(field => field.setAttribute('data-compact-input', ''));
      this.$el
        .querySelectorAll?.('[data-bf-amount] input')
        .forEach(input => input.setAttribute('inputmode', 'decimal'));
      this.$el.querySelector?.('[data-test="bf-mobile"] input')?.setAttribute('inputmode', 'tel');
      // giving-api's limit on the sow text. Set here rather than through DsTextarea's
      // `maxLength`, which also draws a character counter the frame does not have.
      this.$el
        .querySelector?.('[data-test="bf-sow-card"] textarea')
        ?.setAttribute('maxlength', String(BF_SOW_MAX_LENGTH));
    },

    // A member who has pledged (or whose spouse pledged for both) sees their pledge
    // instead. If the check itself fails the store has already said so, and the form
    // stays usable: Submit gets the API's own answer.
    // The pledge window (BF_PLEDGE_OPEN_FLAG) is re-read alongside, so Continue
    // reflects it as it is now, not as it was when the app launched.
    async checkEligibility() {
      const [eligibility] = await Promise.all([
        this.$store.dispatch('buildingFundPledge/fetchEligibility'),
        this.$store.dispatch('buildingFundPledge/refreshPledgeWindow'),
      ]);
      if (!eligibility || eligibility.isEligible) {
        this.closed = false;
        return;
      }
      const pledge = await this.$store.dispatch('buildingFundPledge/fetchPledge');
      if (pledge) {
        this.$router.replace(BF_PLEDGE_ROUTES.view);
        return;
      }
      this.closed = true;
    },

    back() {
      this.$router.replace('/my-giving');
    },

    amountError(key) {
      const invalid = this.errors.amounts?.includes(key);
      return invalid && (this.attempted || this.blurred[key]) ? BF_PLEDGE_ERRORS.amount : '';
    },

    // Same timing as the amount errors: after Submit, or once the field is left.
    partnerError(field) {
      const message = this.errors.partner?.[field];
      return message && (this.attempted || this.blurred[`partner.${field}`]) ? message : '';
    },

    onPartnerBlur(field) {
      this.blurred = { ...this.blurred, [`partner.${field}`]: true };
    },

    // An amount on one side of "one-time OR five months" clears the other, whose
    // fields are then disabled: what is left there (a 0, or text that is not an
    // amount) is not sent, so it is not shown either.
    onOneTimeInput(value) {
      this.form.oneTime = value;
      if (this.oneTimeActive) this.clearMonths();
    },

    onOneTimeBlur() {
      this.blurred = { ...this.blurred, oneTime: true };
      this.form.oneTime = formatAmount(this.form.oneTime);
    },

    clearMonths() {
      this.form.months = splitTotal('');
      this.totalInput = '';
      this.totalTyped = false;
      this.editedMonths = [];
      this.blurred = {
        ...this.blurred,
        total: false,
        ...Object.fromEntries(this.months.map(({ key }) => [key, false])),
      };
    },

    clearOneTime() {
      this.form.oneTime = '';
      this.blurred = { ...this.blurred, oneTime: false };
    },

    // A total typed in is split evenly over the months, and becomes what a changed
    // month is balanced against. Cleared, the months clear and add up again.
    onTotalInput(value) {
      this.totalInput = value;
      this.totalTyped = value.trim() !== '';
      this.editedMonths = [];
      if (!this.totalInvalid) this.form.months = splitTotal(value);
      if (this.monthsActive) this.clearOneTime();
    },

    onTotalBlur() {
      this.blurred = { ...this.blurred, total: true };
      if (this.totalTyped) this.totalInput = formatAmount(this.totalInput);
    },

    // DS Input emits whatever its field holds. `input` is not one of its events,
    // so this lands on its root; in the capture phase it trims the field before
    // DS Input reads it, so the field, the emitted value and the model agree.
    sanitizeAmountField(event) {
      event.target.value = sanitizeAmountInput(event.target.value);
    },

    // With a typed total, the other months move to keep the sum; months typed in
    // past the total raise it. With none, the total just adds the months up.
    onMonthInput(key, value) {
      this.form.months[key] = value;
      if (this.monthsActive) this.clearOneTime();
      if (!this.totalTyped || this.totalInvalid) return;
      if (!this.editedMonths.includes(key)) this.editedMonths.push(key);
      this.form.months = rebalanceMonths(this.form.months, key, this.totalInput, this.editedMonths);
      const sum = monthTotal(this.form.months);
      if (sum > parseAmount(this.totalInput)) this.totalInput = formatAmount(String(sum));
    },

    onAmountBlur(key) {
      this.blurred = { ...this.blurred, [key]: true };
      this.form.months[key] = formatAmount(this.form.months[key]);
    },

    // Unlocks the three details: from what the profile has the first time, from
    // what was saved after that.
    editDetails() {
      if (!this.form.details) {
        this.form.details = { ...this.prefilledDetails };
        this.form.detailsPrefill = { ...this.prefilledDetails };
      }
      // A number it cannot read, or none, opens on Singapore.
      this.mobileCountry = splitContactNumber(this.form.details.mobileNumber).iso2;
      this.editingDetails = true;
    },

    onMobileCountry(iso2) {
      this.mobileCountry = iso2;
      this.setMobile(this.mobileNational);
    },

    onMobileNumber(national) {
      this.setMobile(national);
    },

    setMobile(national) {
      const dialCode = dialCodeForCountry(this.mobileCountry) || DEFAULT_DIAL_CODE;
      this.form.details.mobileNumber = formatContactNumber(dialCode, national);
    },

    onMobileBlur() {
      this.blurred = { ...this.blurred, mobile: true };
    },

    // Locks them again. Nothing is sent until Submit. A field left blank shows its
    // pre-filled value again, which is what Submit would send for it. The CG cannot
    // be edited, so it always holds its pre-filled value.
    // A number that is not a real one keeps them open, with its error showing.
    saveDetails() {
      this.onMobileBlur();
      if (this.errors.mobile) return;
      Object.keys(this.form.details).forEach(key => {
        const value = this.form.details[key].trim();
        this.form.details[key] = value || this.form.detailsPrefill[key];
      });
      this.editingDetails = false;
    },

    openPrivacy() {
      openUrl(
        { url: BF_PLEDGE_PRIVACY_URL },
        { source: 'bf-pledge-form', page: 'BuildingFundPledgeForm', title: 'Privacy Policy' },
      );
    },

    // Every pledge is checked on the confirm page, which sends it. It is pushed,
    // so its back returns to this form as the member left it.
    submit() {
      // The button is disabled then; this covers a tap that lands as it disables.
      if (!this.canSubmit) return;
      this.attempted = true;
      if (Object.keys(this.errors).length) {
        this.scrollToFirstError();
        return;
      }

      // Unedited, the details go as the page shows them now. Edited, the form
      // already holds them, with what they were pre-filled from as the fallback.
      const form = this.form.details
        ? this.form
        : { ...this.form, detailsPrefill: { ...this.prefilledDetails } };
      this.$store.dispatch('buildingFundPledge/reviewPledge', form);
      this.$router.push(BF_PLEDGE_ROUTES.confirm);
    },

    // Only field errors are left once Submit is enabled: an amount that is not a
    // number, or the partner's details.
    scrollToFirstError() {
      let card = this.$refs.coupleCard?.$el;
      if (this.errors.amounts?.includes('oneTime')) card = this.$refs.oneTimeCard?.$el;
      else if (this.errors.amounts || this.errors.total) card = this.$refs.monthsCard?.$el;
      else if (this.errors.mobile && !this.errors.partner) {
        card = this.$el.querySelector?.('[data-test="bf-mobile"]');
      }
      card?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    },
  },
};
</script>
